/**
 * MenuItemViewBuilderModal — modal projektowania widoku LISTY pozycji menu szablonu.
 *
 * Otwierany przez TemplateMenuBuilderPage gdy super admin klika „Projektuj widok listy"
 * na pozycji menu type='module'. Zapisuje wynik do node.view.listConfig w lokalnym
 * stanie drzewa menu (nie do backendu encji szablonu).
 *
 * Przepływ zapisu:
 *  1. Modal dostarcza do ReferenceListContext handler `onListConfigSave`.
 *  2. useTableLayout (saveMutation) wykrywa handler i wywołuje go zamiast PATCH API.
 *  3. Handler wywołuje `onSave(listConfig)` przekazany przez parenta (TemplateMenuBuilderPage).
 *  4. Parent robi updateInTree(nodeId, n => ({ ...n, view: { listConfig, v: 1 } })).
 *  5. isDirty flipuje się (menuItemsEqual porównuje view przez JSON.stringify).
 *  6. Istniejący przycisk „Zapisz menu" zapisuje całość do backendu.
 *
 * Zakres F3a Faza 1: TYLKO listConfig. formConfig i baseFilters = kolejne etapy.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  kartotekaExcludedListFieldSlugs,
  type ListConfig,
  type MenuItem,
  type ModuleTemplateEntityDefinition,
  replacementColumnId,
} from '@saas/shared';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ReferenceListContext } from '@/contexts/ReferenceListContext';
import { useKartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';
import { templateTableId } from '@/lib/module-templates/template-layouts';
import { SystemModuleListBuilderPage } from '@/pages/system-modules/SystemModuleListBuilderPage';

import { BuilderOverlay } from './BuilderOverlay';

interface MenuItemViewBuilderModalProps {
  /** Pozycja menu, dla której projektujemy widok. */
  node: MenuItem;
  /** Kod szablonu (slug) — z useParams w TemplateMenuBuilderPage. */
  templateCode: string;
  /** Lista encji szablonu — do rozwiązania encji na podstawie node.moduleCode. */
  entities: ModuleTemplateEntityDefinition[];
  /**
   * Slug nazwanego widoku listy (z entity.views, type='list') otwieranego w builderze.
   * null = brak (używa syntetycznego viewSlug z node.id — stara ścieżka inline).
   * Gdy podany: builder ładuje seed z entity.views[slug].listConfig zamiast node.view.listConfig.
   */
  namedViewSlug?: string | null;
  /**
   * Wywołane gdy user zatwierdzi widok (kliknie „Zapisz widok" wewnątrz buildera).
   * Parent aktualizuje node.view.listConfig lub named view w lokalnych entities.
   */
  onSave: (listConfig: ListConfig) => void;
  /** Zamknięcie bez zapisu. */
  onClose: () => void;
  /**
   * Gdy true: builder otwiera się w trybie podglądu (read-only).
   * Przycisk Zapisz jest ukryty. Panele są nieedytowalne (pointer-events: none).
   * Domyślnie false = zachowanie bez zmian (edytowalny).
   */
  readOnlyMode?: boolean;
}

export function MenuItemViewBuilderModal({
  node,
  templateCode,
  entities,
  namedViewSlug = null,
  onSave,
  onClose,
  readOnlyMode = false,
}: MenuItemViewBuilderModalProps) {
  const { t } = useTranslation();

  // Wyznacz entitySlug z moduleCode: moduleCode = `${templateCode}_${entitySlug}`
  const entitySlug =
    node.moduleCode && node.moduleCode.startsWith(`${templateCode}_`)
      ? node.moduleCode.slice(templateCode.length + 1)
      : null;

  const entity = entitySlug ? entities.find((e) => e.slug === entitySlug) : null;

  // Encja-odwołanie do kartoteki systemowej (ddlStrategy='reference_to_system_entity').
  // Rozpoznanie: ddlStrategy (nie fields.length — new_table też może mieć pola > 0).
  const isReference =
    entity !== null && entity !== undefined && entity.ddlStrategy === 'reference_to_system_entity';
  const kartotekaSlug = entity?.referenceSystemModuleSlug ?? undefined;

  return (
    <MenuItemViewBuilderModalInner
      node={node}
      templateCode={templateCode}
      entity={entity ?? null}
      entitySlug={entitySlug}
      isReference={isReference}
      kartotekaSlug={kartotekaSlug}
      namedViewSlug={namedViewSlug}
      onSave={onSave}
      onClose={onClose}
      readOnlyMode={readOnlyMode}
      t={t}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner — wyodrębnione żeby hook useKartotekaDefinition nie był wywoływany
// warunkowo (reguła hooks). Inner zawsze renderuje, ale query enabled=false gdy
// brak kartotekaSlug.
// ---------------------------------------------------------------------------

function MenuItemViewBuilderModalInner({
  node,
  templateCode,
  entity,
  entitySlug,
  isReference,
  kartotekaSlug,
  namedViewSlug,
  onSave,
  onClose,
  readOnlyMode,
  t,
}: {
  node: MenuItem;
  templateCode: string;
  entity: ModuleTemplateEntityDefinition | null;
  entitySlug: string | null;
  isReference: boolean;
  kartotekaSlug: string | undefined;
  namedViewSlug: string | null;
  onSave: (listConfig: ListConfig) => void;
  onClose: () => void;
  readOnlyMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // useKartotekaDefinition tylko dla encji-odwołania do kartoteki systemowej.
  // Dla new_table slug=undefined → query disabled (hooks zawsze wywoływane).
  const kartotekaQuery = useKartotekaDefinition(isReference ? kartotekaSlug : undefined);
  const kartotekaFields = kartotekaQuery.data?.fields ?? [];
  const kartotekaDefaultListConfig = kartotekaQuery.data?.defaultListConfig;

  // Pełna lista pól dostępnych do palety „Dodaj kolumnę":
  //  - reference_to_system_entity: pola kartoteki (z useKartotekaDefinition) — undefined
  //    DOPÓKI zapytanie nie ma potwierdzonych danych (kartotekaQuery.data), nie `[]` z
  //    domyślnego fallbacku. `[]` jest truthy — soft-heal w ListColumnsPanel (`allowedColumnIds
  //    = availableFields ? ... : undefined`) traktowałby stan ładowania jak „katalog
  //    potwierdzony pusty" i przy zapisie w tym oknie wyciąłby WSZYSTKIE kolumny (core+ext)
  //    z zapisanego list_config, zamiast poczekać na katalog.
  //  - new_table: pola własne encji szablonu (synchroniczne z `entities` prop — bez ryzyka
  //    stanu ładowania, `entity?.fields ?? []` zostaje).
  //  - z palety odpada pole wykluczone z listy klienta BEZ kolumny zastępczej (np. pola umowy,
  //    ADR-0109b): lista klienta go nie narysuje, a serwer odrzuciłby układ z nim przy zapisie
  //    (`kartotekaListColumnIds`). `company_id`/`category_id` zostają — zapis zamienia je na
  //    kolumny Firma/Kategoria (`normalizeKartotekaListConfig`).
  const listHiddenFieldSlugs = new Set(
    isReference && kartotekaSlug
      ? kartotekaExcludedListFieldSlugs(kartotekaSlug).filter(
          (fieldSlug) => replacementColumnId(kartotekaSlug, fieldSlug) === null,
        )
      : [],
  );
  const availableFields: Array<{ id: string; name: string }> | undefined = isReference
    ? kartotekaQuery.data
      ? kartotekaFields
          .filter((f) => !listHiddenFieldSlugs.has(f.slug))
          .map((f) => ({ id: f.slug, name: f.name }))
      : undefined
    : (entity?.fields ?? []).map((f) => ({ id: f.slug, name: f.name }));

  // Seed: jeśli node.view.listConfig jest już ustawione, jest to initial config.
  // Dla builder-a przekazujemy go przez templateTableId — builder samodzielnie
  // go odczyta z danych szablonu (view.listConfig żyje w node, ale builder idzie
  // do cache widoku encji szablonu). Jednak w trybie menu-embed NIE chcemy strzału
  // do backendu encji — chcemy, żeby seed był z node.view.listConfig lub domyślny.
  //
  // Rozwiązanie: viewSlug = 'menu_item_list_<nodeId_skrócone>'. Taki viewSlug nie
  // istnieje w tabeli widoków encji szablonu, więc useTableLayout zwróci pusty layout
  // (lub seed z kartotekaDefaultListConfig). Seed inicjalizujemy raz przez useTableLayout
  // (ścieżka isReferenceWithCtx w useTableLayout.ts) lub seedTemplateListConfig.
  // Efekt: builder startuje z node.view.listConfig jeśli jest, inaczej default.
  //
  // UWAGA: ta ścieżka działa bo:
  //  a) onListConfigSave przechwytuje zapis → nie strzela PATCH encji
  //  b) queryKey cache jest per templateTableId → izolowany od encji
  //  c) layoutConfig z szablonu dla nowego viewSlug = pusty → seed z ctx
  //
  // Alternatywa prościej: synthetic tableId nie jako template: ale jako stały
  // stały klucz — ale to by mogło kolidować z innymi widokami. Używamy node.id
  // jako część viewSlug: bezpieczne (UUID).
  // Gdy otwieramy nazwany widok listy: używamy jego slugu (builder ładuje config z entity.views).
  // Gdy brak nazwanego slugu (inline / domyślny): syntetyczny slug per node (izolowany cache).
  const effectiveViewSlug = namedViewSlug ?? `menu_${node.id.replace(/-/g, '').slice(0, 12)}`;
  const tableId = entitySlug ? templateTableId(templateCode, entitySlug, effectiveViewSlug) : null;

  // Strażnik niezapisanych zmian: dirty = true gdy user zmodyfikował kolumny
  // w builderze listy (sygnalizowane przez onDirtyChange z ListColumnsPanel przez
  // ReferenceListContext). Zerowane po udanym zapisie.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  // Handler zapisu z buildera → przekazuje listConfig do parenta.
  const handleListConfigSave = (listConfig: ListConfig) => {
    onSave(listConfig);
  };

  const title = t('admin.templateMenuBuilder.viewBuilder.title');

  return (
    <>
      <BuilderOverlay
        testId="menu-item-view-builder-modal"
        onRequestClose={handleRequestClose}
        width="wide"
        title={title}
        subtitle={node.moduleCode ? (node.label ?? node.moduleCode) : undefined}
        banner={
          readOnlyMode ? (
            <span
              className="rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
              data-testid="view-builder-read-only-banner"
            >
              {t('admin.menuItemViews.readOnlyBanner', {
                defaultValue: 'Podgląd — tylko do odczytu, nie można edytować',
              })}
            </span>
          ) : undefined
        }
        closeLabel={t('admin.templateMenuBuilder.viewBuilder.close')}
        closeTestId="menu-item-view-builder-close"
      >
        {!entity || !entitySlug ? (
          <p className="text-sm text-text-muted">
            {t('admin.templateMenuBuilder.viewBuilder.entityNotFound')}
          </p>
        ) : !tableId ? (
          <p className="text-sm text-text-muted">
            {t('admin.templateMenuBuilder.viewBuilder.entityNotFound')}
          </p>
        ) : (
          <ReferenceListContext.Provider
            value={{
              kartotekaFields,
              kartotekaDefaultListConfig,
              availableFields,
              onViewConfigPersisted: () => {
                // W trybie menu-embed onViewConfigPersisted nie jest używany
                // (onListConfigSave przechwytuje zapis wcześniej). No-op.
              },
              // readOnlyMode: brak ścieżki zapisu — onListConfigSave=undefined blokuje
              // interceptor w useTableLayout (saveMutation nie wywoła handlera parenta).
              onListConfigSave: readOnlyMode ? undefined : handleListConfigSave,
              onDirtyChange: setHasUnsavedChanges,
              // Built-iny record_name/record_number są realnymi polami TYLKO dla
              // encji new_table (własne pola szablonu — fizyczna kolumna nazwy/numeru
              // rekordu). Dla reference_to_system_entity pola żyją w kartotece
              // systemowej i nie mają takich kolumn — bramka palety w ListColumnsPanel.
              builtinColumnsAvailable: !isReference,
              // Realny slug kartoteki systemowej (nie `slug` menu-embed, patrz JSDoc
              // `columnRegistrySlug`) — chroni kolumny spoza katalogu (np. `company`)
              // przed soft-healem w ListColumnsPanel. Tylko dla reference_to_system_entity;
              // new_table ma osobny slug-space modułów własnych (§4.10 CLAUDE.md).
              columnRegistrySlug: isReference ? kartotekaSlug : undefined,
            }}
          >
            <SystemModuleListBuilderPage
              scope="template"
              embeddedMode
              code={templateCode}
              entitySlug={entitySlug}
              viewSlug={effectiveViewSlug}
              readOnlyMode={readOnlyMode}
              // HIDDEN (tymczasowo): ukryj „+ Dodaj widget" w builderze widoku listy pozycji
              // menu — nazwane widoki menu nie są propagowane do tenanta (krok D nadal TODO),
              // więc dodawanie widgetów tu byłoby zapisem „w próżnię". Sama zakładka „Widgety"
              // i sekcja „Domyślny wygląd kart" zostają widoczne.
              hideAddWidget
            />
          </ReferenceListContext.Provider>
        )}
      </BuilderOverlay>

      <ConfirmDialog
        open={showCloseConfirm}
        variant="danger"
        title={t('admin.templateMenuBuilder.viewBuilder.confirmClose.title')}
        message={t('admin.templateMenuBuilder.viewBuilder.confirmClose.message')}
        confirmLabel={t('admin.templateMenuBuilder.viewBuilder.confirmClose.confirm')}
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onClose={() => setShowCloseConfirm(false)}
      />
    </>
  );
}
